<template>
    <div class="tooltip-wrapper">
        <div
            :ref="setTooltipContainer"
            class="tooltip-container"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
            @focus="showTooltipDelay"
            @blur="hideTooltip"
        >
            <slot />
            <div
                v-if="isTouchDevice"
                :ref="setTooltipContainer"
                class="tooltip-icon"
            >
                <i
                    class="icon mi-circle-information"
                    @touchstart="handleTouchStart"
                />
            </div>
        </div>
        <div
            ref="tooltip"
            class="tl-tooltip"
            :class="{ secondary: secondary }"
            :style="tooltipStyles"
        >
            <slot name="tooltip">{{ tip }}</slot>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { type Strategy, useFloating, flip, shift, offset } from '@floating-ui/vue'
import { onClickOutside, useEventBus } from '@vueuse/core'
import { getScrollParent } from 'widget-components/utilities/get-scroll-parent'

type TooltipProps = {
    tip?: string
    placement?: 'top' | 'left' | 'right' | 'bottom'
    delay?: number | string
    strategy?: Strategy
    disabled?: boolean
    zIndex?: number | string
    secondary?: boolean
}

const props = defineProps<TooltipProps>()

const tooltipContainer = ref<HTMLElement | null>()
const tooltip = ref<HTMLElement>()
const isOpened = ref<boolean>(false)
const delayTimeout = ref<number>()
const isTouchDevice = ref<boolean>('ontouchstart' in window || navigator.maxTouchPoints > 0)
let stopClickOutsideVueUse: () => void
const componentId = Symbol('componentId')

const { emit, once } = useEventBus<'tooltip-show', TooltipEventBusEventPayload>('widget-components-tooltip-show')

type TooltipEventBusEventPayload = {
    componentId: symbol
}

const tooltipPlacement = computed(() => {
    if (props.placement === 'bottom') {
        return 'bottom'
    }

    if (props.placement === 'left') {
        return 'left'
    }

    if (props.placement === 'right') {
        return 'right'
    }

    return 'top'
})

const { floatingStyles, update } = useFloating(tooltipContainer, tooltip, {
    placement: tooltipPlacement.value,
    strategy: props.strategy ? props.strategy : 'fixed',
    middleware: [flip(), shift(), offset(isTouchDevice.value ? -5 : 5)],
})

const tooltipStyles = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const styles: Record<string, any> = {
        ...floatingStyles.value,
    }

    if (isOpened.value) {
        styles.zIndex = props.zIndex || '1000'
        styles.opacity = '1'
        styles.visibility = 'visible'
        styles.pointerEvents = 'all'
    } else {
        styles.zIndex = '-1'
        styles.opacity = '0'
        styles.visibility = 'hidden'
        styles.pointerEvents = 'none'
    }

    return styles
})

const showTooltip = () => {
    if (isOpened.value) return

    isOpened.value = true
    update()
    emit('tooltip-show', {
        componentId: componentId,
    })

    if (isTouchDevice.value) {
        once((event, data) => {
            if (event === 'tooltip-show') {
                if (data?.componentId && data.componentId !== componentId && isOpened.value) {
                    hideTooltip()
                }
            }
        })
        stopClickOutsideVueUse = onClickOutside(tooltipContainer, hideTooltip)
        getScrollParent(tooltipContainer.value!)?.addEventListener('scroll', hideTooltip, { passive: true })
    }
}

const setTooltipContainer = (el: HTMLElement | null) => {
    tooltipContainer.value = isTouchDevice.value && el?.classList.contains('tooltip-icon') ? el : el
    return tooltipContainer
}

const clearCurrentTimeout = () => {
    if (delayTimeout.value !== null) clearTimeout(delayTimeout.value)
}

const handleMouseEnter = () => {
    if (isTouchDevice.value || isOpened.value) {
        return
    }
    showTooltipDelay()
}

const handleMouseLeave = () => {
    if (isTouchDevice.value) {
        return
    }
    hideTooltip()
}

const handleTouchStart = (event: TouchEvent) => {
    event.stopPropagation()

    if (!isTouchDevice.value) {
        return
    }

    if (isOpened.value) {
        hideTooltip()
        return
    }

    showTooltipDelay()
}

function showTooltipDelay() {
    if (props.disabled) {
        return
    }

    if (props.delay) {
        clearCurrentTimeout()
        delayTimeout.value = window.setTimeout(showTooltip, Number(props.delay))
    } else {
        showTooltip()
    }
}

function hideTooltip() {
    clearCurrentTimeout()
    isOpened.value = false
    if (stopClickOutsideVueUse) {
        stopClickOutsideVueUse?.()
    }
    getScrollParent(tooltipContainer.value!)?.removeEventListener('scroll', hideTooltip)
}
</script>

<style lang="sass" scoped>
.tooltip-wrapper
    width: 100%

.tl-tooltip
    max-width: 400px
    background: var(--color-black)
    color: var(--color-white)
    padding: 8px 16px
    border-radius: 8px
    transition: opacity .3s ease-in-out
    font-size: 16px
    font-weight: normal
    word-break: break-word

    &.secondary
        background: var(--color-white)
        padding: 8px
        border-radius: 8px
        border: 1px solid var(--color-black-tint-light)
        box-shadow: 0 0 10px 0 var(--color-black-tint-medium)
        color: var(--color-black)

@media (hover: none)
    .tooltip-container
        position: relative
        flex: 1
        padding-right: 25px

.tooltip-icon
    position: absolute
    top: 0
    right: 0
    margin: -15px
    padding: 15px
    z-index: 100
</style>
